import { FC, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Collapse,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { Userbot } from "../../interfaces"
import { useCatalog } from "../../hooks"
import { FieldSpecResult } from "../fields/fieldSpecResult"
import { actionableStepHint } from "./actionableStepHint"
import { ChainProgressPanel } from "./chainProgressPanel"

interface IProps {
  step: Userbot.ChainReportStep
}

/**
 * Блок детальной информации о результате и ошибках развёрнутой строки шага (SPEC §4.1).
 */
export const StepResultDetails: FC<IProps> = ({ step }) => {
  const catalog = useCatalog()
  const navigate = useNavigate()
  const [rawOpen, setRawOpen] = useState(false)

  const descriptor = catalog.actions.find((a) => a.name === step.action)
  const hint = actionableStepHint(step)
  const hasStructuredResult =
    Boolean(descriptor?.result && descriptor.result.length > 0) &&
    step.result !== null &&
    step.result !== undefined

  const resObj =
    step.result && typeof step.result === "object"
      ? (step.result as Record<string, unknown>)
      : null

  const phase2ChainId =
    typeof resObj?.phase2ChainId === "string" ? resObj.phase2ChainId : null

  const remainingAuths =
    typeof resObj?.remainingAuthorizations === "number"
      ? resObj.remainingAuthorizations
      : null

  const orphanedRefs =
    resObj?.orphanedRefs && typeof resObj.orphanedRefs === "object"
      ? (resObj.orphanedRefs as Record<string, number>)
      : null

  const orphanedEntries = orphanedRefs
    ? Object.entries(orphanedRefs).filter(
        ([, v]) => typeof v === "number" && v > 0,
      )
    : []

  return (
    <Stack
      spacing={1.5}
      sx={{ py: 1, px: 2, bgcolor: "action.hover", borderRadius: 1 }}
    >
      {hint && (
        <Alert severity={hint.linkToAccount ? "warning" : "info"}>
          {hint.text}
          {hint.linkToAccount && (
            <Link
              component="button"
              type="button"
              sx={{ ml: 1, fontWeight: "bold", cursor: "pointer" }}
              onClick={() => navigate("/userbot/accounts")}
            >
              Открыть карточку аккаунта
            </Link>
          )}
        </Alert>
      )}

      {remainingAuths !== null && remainingAuths > 1 && (
        <Alert severity="warning">
          Осталось нетерминированных сессий: {remainingAuths} (ожидалась 1).
          Возможно, старые сессии не были завершены.
        </Alert>
      )}

      {orphanedEntries.length > 0 && (
        <Alert severity="warning">
          Осиротевшие ссылки:{" "}
          {orphanedEntries.map(([k, v]) => `${k}: ${v}`).join(", ")}. Требуется
          ручная перепривязка (PATCH clientId → role).
        </Alert>
      )}

      {phase2ChainId && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.paper" }}>
          <Stack spacing={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2">
                Фаза 2 онбординга (сброс сессий)
              </Typography>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() =>
                  navigate(`/userbot/chains/report/${phase2ChainId}`)
                }
                sx={{ cursor: "pointer", fontFamily: "monospace" }}
              >
                Открыть цепочку ({phase2ChainId.slice(0, 8)}…)
              </Link>
            </Box>
            <ChainProgressPanel chainId={phase2ChainId} />
          </Stack>
        </Paper>
      )}

      {hasStructuredResult && descriptor?.result ? (
        <FieldSpecResult specs={descriptor.result} value={step.result} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {step.result !== null && step.result !== undefined
            ? "Структурной схемы результата нет в каталоге — доступен raw JSON."
            : "Результат отсутствует."}
        </Typography>
      )}

      {step.result !== null && step.result !== undefined && (
        <Box>
          <Button
            size="small"
            variant="text"
            onClick={() => setRawOpen((v) => !v)}
            sx={{ textTransform: "none", p: 0 }}
          >
            {rawOpen ? "Скрыть raw JSON" : "Показать raw JSON"}
          </Button>
          <Collapse in={rawOpen} unmountOnExit>
            <Box mt={1}>
              <TextField
                value={JSON.stringify(step.result, null, 2)}
                multiline
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  style: { fontFamily: "monospace", fontSize: "0.8rem" },
                }}
              />
            </Box>
          </Collapse>
        </Box>
      )}
    </Stack>
  )
}
